import { useEffect, useState } from "react";
import { useEditorCommands, type NativeFileHandle } from "@linempo/react-state";
import type { AssetDescriptor } from "@linempo/project-domain";
import { errorMessage } from "../../editor-geometry.js";

export interface Thumbnail { readonly url?: string; readonly error?: string }
/** At most 32 small leases, released on page/project changes, unmount and late arrivals. */
export function useThumbnails(assets: readonly AssetDescriptor[]) {
  const commands = useEditorCommands();
  const [thumbnails, setThumbnails] = useState<ReadonlyMap<string, Thumbnail>>(new Map());
  const [retryKey, retry] = useState(0);
  const key = assets.slice(0, 32).map((asset) => `${asset.id}:${asset.hash}`).join("|");
  useEffect(() => {
    let active = true;
    const leases: NativeFileHandle<AssetDescriptor>[] = [];
    setThumbnails(new Map());
    void (async () => {
      for (const asset of assets.slice(0, 32)) {
        if (!active) break;
        try {
          const handle = await commands.readThumbnail(asset);
          if (!active) { handle.release(); break; }
          leases.push(handle);
          setThumbnails((previous) => new Map(previous).set(asset.id, { url: handle.url }));
        } catch (error) {
          if (active) setThumbnails((previous) => new Map(previous).set(asset.id, { error: errorMessage(error) }));
        }
      }
    })();
    return () => { active = false; for (const lease of leases) lease.release(); };
    // Asset identity/content is the lease key; metadata revisions do not reacquire URLs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commands, key, retryKey]);
  return { thumbnails, retry: () => retry((value) => value + 1) };
}
